import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';

import HelpSection from './components/HelpSection';
import SearchBar from './components/SearchBar';
import QuickNav from './components/QuickNav';
import FAQSection from './components/FAQSection';

const HelpSupport = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('profile');
  const [searchQuery, setSearchQuery] = useState('');

  // Help content data for all six sections
  const helpSections = [
    {
      id: 'profile',
      name: 'Profile',
      icon: 'User',
      description: 'Basic information and contact details',
      content: {
        overview: 'The Profile section contains your basic artist information that appears throughout your mobile app.',
        steps: [
          {
            title: 'Artist Name',
            description: 'Enter your professional artist name as you want it displayed in the app.',
            tips: ['Use your stage name or band name', 'Keep it consistent across all platforms', 'Avoid special characters that might not display properly']
          },
          {
            title: 'Artist Type',
            description: 'Select your primary artistic category (Music, Visual, Model, or Actor).',
            tips: ['Choose the category that best represents your main work', 'This affects which work sections are available to you']
          },
          {
            title: 'Contact Information',
            description: 'Add your professional email and phone number for booking inquiries.',
            tips: ['Use a professional email address', 'Ensure phone number is current', 'Consider using a business line for privacy']
          },
          {
            title: 'Social Media Links',
            description: 'Connect your social media profiles to increase your online presence.',
            tips: ['Use full URLs (https://)', 'Verify links work before saving', 'Keep profiles active and updated']
          }
        ],
        validation: ['Artist name is required', 'Email must be valid format', 'At least one contact method required']
      }
    },
    {
      id: 'promotion',
      name: 'Promotion',
      icon: 'Megaphone',
      description: 'App images and featured content',
      content: {
        overview: 'Promotional content includes splash screens, logos, and featured items that represent your brand.',
        steps: [
          {
            title: 'Splash Screen Images',
            description: 'Upload high-quality images that appear when users open your app.',
            tips: ['Use images 1080x1920px for best results', 'Multiple images create a slideshow effect', 'Choose images that represent your brand']
          },
          {
            title: 'Logo Upload',
            description: 'Your logo appears in various places throughout the app.',
            tips: ['Use PNG format with transparent background', 'Minimum 500x500px resolution', 'Keep it simple and recognizable']
          },
          {
            title: 'Featured Items',
            description: 'Highlight your most important work or achievements.',
            tips: ['Choose your best work', 'Update regularly to keep content fresh', 'Use high-quality images']
          }
        ],
        validation: ['At least one splash image required', 'Logo must be under 5MB', 'Images must be JPG, PNG, or WebP format']
      }
    },
    {
      id: 'about',
      name: 'About',
      icon: 'FileText',
      description: 'Biography and booking information',
      content: {
        overview: 'The About section tells your story and provides booking information for potential clients.',
        steps: [
          {
            title: 'Biography',
            description: 'Write a compelling artist biography that showcases your experience and style.',
            tips: ['Keep it engaging but professional', 'Include your artistic journey', 'Mention notable achievements', 'Update regularly as you grow']
          },
          {
            title: 'Profile Images',
            description: 'Add professional photos that represent your brand.',
            tips: ['Use high-resolution images', 'Include variety (headshots, action shots, etc.)', 'Ensure images are well-lit and clear']
          },
          {
            title: 'Booking Contact',
            description: 'Provide contact information for booking inquiries.',
            tips: ['Include manager or agent contact if applicable', 'Specify preferred contact method', 'Include response time expectations']
          }
        ],
        validation: ['Biography text is required', 'At least one contact method needed', 'Images must be under 5MB each']
      }
    },
    {
      id: 'calendar',
      name: 'Calendar',
      icon: 'Calendar',
      description: 'Events and performances',
      content: {
        overview: 'The Calendar section displays your upcoming events, performances, and important dates.',
        steps: [
          {
            title: 'Adding Events',
            description: 'Click "Add Event" to create new calendar entries.',
            tips: ['Include all relevant details', 'Double-check dates and times', 'Add location information when applicable']
          },
          {
            title: 'Event Details',
            description: 'Fill in event title, description, date, time, and optional URL.',
            tips: ['Use descriptive titles', 'Include venue information', 'Add ticket links when available', 'Keep descriptions concise but informative']
          },
          {
            title: 'Managing Events',
            description: 'Edit or delete events by clicking the action buttons.',
            tips: ['Remove past events regularly', 'Update event details as needed', 'Events save automatically when added']
          }
        ],
        validation: ['Event title is required', 'Date must be valid', 'Time format: HH:MM (24-hour)']
      }
    },
    {
      id: 'work',
      name: 'Work',
      icon: 'Briefcase',
      description: 'Portfolio and projects',
      content: {
        overview: 'The Work section showcases your portfolio based on your artist type (Music, Visual, Model, or Actor).',
        steps: [
          {
            title: 'Music Artists',
            description: 'Add tracks, albums, music videos, and streaming links.',
            tips: ['Include high-quality audio samples', 'Add album artwork', 'Link to streaming platforms', 'Organize by release date']
          },
          {
            title: 'Visual Artists',
            description: 'Upload artwork, photography, and creative projects.',
            tips: ['Use high-resolution images', 'Include artwork details', 'Organize by series or theme', 'Add creation dates and medium info']
          },
          {
            title: 'Models',
            description: 'Create a modeling portfolio with professional photos.',
            tips: ['Include variety of shots', 'Professional photography preferred', 'Add shoot details', 'Keep portfolio current']
          },
          {
            title: 'Actors',
            description: 'Showcase reels, headshots, and acting experience.',
            tips: ['Include current headshots', 'Add demo reels or clips', 'List credits and experience', 'Keep resume updated']
          }
        ],
        validation: ['Work items vary by artist type', 'Images must be under 5MB', 'Required fields depend on work type']
      }
    },
    {
      id: 'shop',
      name: 'Shop',
      icon: 'ShoppingCart',
      description: 'Merchandise and products',
      content: {
        overview: 'The Shop section allows you to showcase and sell merchandise or services.',
        steps: [
          {
            title: 'External Store Links',
            description: 'Add links to your existing online stores or marketplaces.',
            tips: ['Use full URLs with https://', 'Test links before saving', 'Include popular platforms', 'Update links if stores change']
          },
          {
            title: 'Featured Products',
            description: 'Highlight your best-selling or newest merchandise.',
            tips: ['Use high-quality product photos', 'Include clear descriptions', 'Add pricing information', 'Update regularly']
          },
          {
            title: 'Contact for Sales',
            description: 'Provide contact information for direct sales inquiries.',
            tips: ['Specify preferred contact method', 'Include response time expectations', 'Consider payment options', 'Be clear about shipping']
          }
        ],
        validation: ['At least one shop method required', 'URLs must be valid format', 'Contact information must be provided']
      }
    }
  ];

  // Filter content based on search query
  const filteredSections = helpSections?.filter(section => 
    searchQuery === '' || 
    section?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    section?.content?.overview?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    section?.content?.steps?.some(step => 
      step?.title?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      step?.description?.toLowerCase()?.includes(searchQuery?.toLowerCase())
    )
  );

  const activeSection = helpSections?.find(section => section?.id === activeTab);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="pt-16">
        <div className="max-w-7xl mx-auto px-6 py-8">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-3xl font-bold text-text-primary">Help & Support</h1>
                <p className="text-text-secondary mt-2">
                  Complete guide to managing your artist mobile app content
                </p>
              </div>
              
              <Button
                variant="outline"
                onClick={() => navigate('/artist-content-management')}
                iconName="ArrowLeft"
                iconPosition="left"
                iconSize={16}
              >
                Back to Content
              </Button>
            </div>

            {/* Search Bar */}
            <SearchBar 
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              placeholder="Search help topics..."
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Quick Navigation */}
            <div className="lg:col-span-1">
              <QuickNav
                sections={filteredSections}
                activeTab={activeTab}
                onTabChange={setActiveTab}
              />
            </div>

            {/* Main Content Area */}
            <div className="lg:col-span-3">
              {activeSection && (
                <HelpSection
                  section={activeSection}
                  searchQuery={searchQuery}
                />
              )}

              {/* FAQ Section */}
              <div className="mt-12">
                <FAQSection />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HelpSupport;